<template>
    <div class="lms__superadmin_users_list_table">
        <component-table
            :labels="tableLabels"
            :items="items"
            :component-table-item="LmsSuperadminUsersListTableItem"
            :component-table-collapse-item="LmsSuperadminUsersListTableItemCollapseTable"
            :component-table-collapse-item-active="activeCollapseIndex"
            @emit-collapse="handleCollapse"
            @emit-login-as-learner="handleLogInAsLearner"
            @emit-cancel="openNotifyCancelInvitation"
            @emit-resend="openNotifyResendInvitation"
            @emit-reset-password="openNotifyResetPassword"
            @emit-reset-mfa="openNotifyResetMfa"
            @emit-remove-superadmin="openNotifyRemoveSuperadmin"
            @emit-remove-masteradmin="openNotifyRemoveMasteradmin"
            @emit-delete-profile="openNotifyRemoveProfile"
            @emit-reactivate-profile="openNotifyReactivateProfile"
            @emit-sort="handleSort"
        />
        <pop-notify
            :class="'pop__notify--resetmfa'"
            ref="popnotifyresetmfa"
            :title-text="$t('lms.superadmin.popNotify.resetMFA')"
            :content-text="$t('lms.superadmin.popNotify.textMFA')"
            :cancel-button-text="$t('lms.superadmin.popNotify.cancel')"
            :submit-button-danger="true"
            :submit-button-text="$t('lms.superadmin.popNotify.resetMFA')"
            :prevent-close="true"
            :submit-button-animate-loading="true"
            :submit-button-animate-loading-name="'reset-mfa'"
            @emit-submit="resetMfa"
        />
        <pop-notify
            :class="'pop__notify--resetpassword'"
            ref="popnotifyresetpassword"
            :title-text="$t('lms.superadmin.popNotify.resetPassword')"
            :content-text="$t('lms.superadmin.popNotify.textPassword')"
            :cancel-button-text="$t('lms.superadmin.popNotify.cancel')"
            :submit-button-danger="true"
            :submit-button-text="$t('lms.superadmin.popNotify.resetPassword')"
            :prevent-close="true"
            :submit-button-animate-loading="true"
            :submit-button-animate-loading-name="'reset-password'"
            @emit-submit="resetPassword"
        />
        <pop-notify
            :class="'pop__notify--cancel'"
            ref="notifycancelinvitation"
            :title-text="'Cancel invitation'"
            :content-text="`This user will not be able to accept the invite if you cancel the invitation. \n\nDo you wish to cancel the invitation?`"
            :cancel-button-text="'Cancel'"
            :submit-button-text="'Cancel invite'"
            :submit-button-danger="true"
            :submit-button-icon="true"
            :submit-button-icon-name="'x-circle'"
            :prevent-close="true"
            @emit-submit="cancelInvitation"
        />
        <pop-notify
            :class="'pop__notify--resend'"
            ref="notifyresendinvitation"
            :title-text="$t(`lms.workspace.notify.titleResend`)"
            :content-text="$t(`lms.workspace.notify.areYouSureResend`)"
            :cancel-button-text="'Cancel'"
            :submit-button-text="'Confirm'"
            :submit-button-danger="true"
            :prevent-close="true"
            @emit-submit="resendInvitation"
        />
        <pop-notify
            :class="'pop__notify--remove-superadmin'"
            ref="notifyremoveadmin"
            :title-text="$t('lms.superadmin.popNotify.removeAdmin')"
            :content-text="$t('lms.superadmin.popNotify.content')"
            :cancel-button-text="$t('lms.superadmin.popNotify.cancel')"
            :submit-button-text="$t('lms.superadmin.popNotify.confirmButton')"
            :submit-button-danger="true"
            :prevent-close="true"
            @emit-submit="removeAdmin"
        />
        <pop-notify
            :class="'pop__notify--remove-masteradmin'"
            ref="notifyremovemasteradmin"
            :title-text="'Remove master administrator'"
            :content-text="'Are you sure want to remove this master administrator?'"
            :cancel-button-text="$t('lms.superadmin.popNotify.cancel')"
            :submit-button-text="$t('lms.superadmin.popNotify.confirmButton')"
            :submit-button-danger="true"
            :prevent-close="true"
            @emit-submit="removeMasteradmin"
        />
        <pop-notify
            :class="'pop__notify--remove-profile'"
            ref="notifyremoveprofile"
            :title-text="'Delete profile?'"
            :content-text="`Deleting this profile will cancel any enrolments or bookings the profile might have. \n\nDo you wish to delete this profile?`"
            :cancel-button-text="$t('lms.superadmin.popNotify.cancel')"
            :submit-button-text="'Delete profile'"
            :submit-button-icon="true"
            :submit-button-icon-name="'trash-2'"
            :submit-button-icon-align="'right'"
            :submit-button-danger="true"
            :prevent-close="true"
            @emit-submit="removeProfile"
        />
        <pop-notify
            :class="'pop__notify--reactivate'"
            ref="notifyreactivateprofile"
            :title-text="'Re-activate profile?'"
            :content-text="`Re-activating this profile will allow the user to log back in to this workspace. \n\nDo you wish to reactivate this profile?`"
            :cancel-button-text="$t('lms.superadmin.popNotify.cancel')"
            :submit-button-text="'Re-activate profile'"
            :submit-button-icon="true"
            :submit-button-icon-name="'refresh-ccw'"
            :submit-button-icon-align="'right'"
            :prevent-close="true"
            @emit-submit="reactivateProfile"
        >
          <component-note v-if="notifyStoreValue && notifyStoreValue.status === 'expired'">The expiration date for this profile will be removed. You can assign a new expiry date by editing re-activated profile.</component-note>
        </pop-notify>
    </div>
</template>

<script>
import LmsSuperadminUsersListTableItem from '@/components/lms/superadmin/users/list/LmsSuperadminUsersListTableItem'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentTable from '@/components/default/shared/ComponentTable'
import LmsSuperadminUsersListTableItemCollapseTable from '@/components/lms/superadmin/users/list/LmsSuperadminUsersListTableItemCollapseTable'
import { User } from '@/User'
import ComponentNote from '@/components/default/shared/ComponentNote'

export default {
  name: 'lms-superadmin-users-list-table',
  components: { ComponentNote, ComponentTable, PopNotify },
  data () {
    return {
      user: new User(),
      activeCollapseIndex: '',
      notifyStoreValue: '',
      LmsSuperadminUsersListTableItem,
      LmsSuperadminUsersListTableItemCollapseTable
    }
  },
  props: {
    items: {
      type: Array
    }
  },
  computed: {
    loggedInUserWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    tableLabels () {
      let itemsArr = []

      itemsArr.push({
        key: 'firstName',
        title: 'First name',
        type: 'string',
        sortable: true,
        queryable: true
      })
      itemsArr.push({
        key: 'lastName',
        title: 'Last name',
        type: 'string',
        sortable: true,
        queryable: true
      })
      itemsArr.push({
        key: 'emailAddress',
        title: 'Email address',
        type: 'string',
        sortable: true,
        queryable: true
      })
      if (this.loggedInUserWorkspaceId) {
        itemsArr.push({
          key: 'roles',
          title: 'Role',
          type: 'string'
        })
        itemsArr.push({
          key: 'orgUnits',
          title: 'Org unit',
          type: 'string'
        })
        itemsArr.push({
          key: 'groups',
          title: 'Group',
          type: 'string'
        })
        itemsArr.push({
          key: 'status',
          title: 'Status',
          type: 'string',
          sortable: true,
          queryable: true
        })
      }
      itemsArr.push({
        key: 'actions',
        title: '',
        type: 'string'
      })

      return itemsArr
    }
  },
  methods: {
    handleSort (data) {
      this.$emit('emit-sort', data)
    },
    handleCollapse (value) {
      this.activeCollapseIndex = this.activeCollapseIndex !== value ? value : ''
    },
    handleLogInAsLearner (data) {
      this.$store.dispatch('authUserImpersonateUser', data)
    },
    openNotifyResetMfa (data) {
      this.notifyStoreValue = data
      this.$refs.popnotifyresetmfa.setStoreValue(data)
      this.$refs.popnotifyresetmfa.openNotify()
    },
    openNotifyResetPassword (data) {
      this.notifyStoreValue = data
      this.$refs.popnotifyresetpassword.setStoreValue(data)
      this.$refs.popnotifyresetpassword.openNotify()
    },
    openNotifyCancelInvitation (data) {
      this.notifyStoreValue = data
      this.$refs.notifycancelinvitation.setStoreValue(data)
      this.$refs.notifycancelinvitation.openNotify()
    },
    openNotifyRemoveSuperadmin (data) {
      this.notifyStoreValue = data
      this.$refs.notifyremoveadmin.setStoreValue(data)
      this.$refs.notifyremoveadmin.openNotify()
    },
    openNotifyRemoveMasteradmin (data) {
      this.notifyStoreValue = data
      this.$refs.notifyremovemasteradmin.setStoreValue(data)
      this.$refs.notifyremovemasteradmin.openNotify()
    },
    openNotifyRemoveProfile (data) {
      this.notifyStoreValue = data
      this.$refs.notifyremoveprofile.setStoreValue(data)
      this.$refs.notifyremoveprofile.openNotify()
    },
    openNotifyReactivateProfile (data) {
      this.notifyStoreValue = data
      this.$refs.notifyreactivateprofile.setStoreValue(data)
      this.$refs.notifyreactivateprofile.openNotify()
    },
    cancelInvitation () {
      const userProfileId = this.$refs.notifycancelinvitation.storeValue
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileCancelInvitation', { id: userProfileId, redirect: false }).then(() => {
        this.$eventBus.$emit('trigger-get-users')
      }).finally(() => {
        if (this.$refs.notifycancelinvitation) {
          this.$refs.notifycancelinvitation.closeNotify()
        }
      })
    },
    openNotifyResendInvitation (data) {
      this.$refs.notifyresendinvitation.setStoreValue(data)
      this.$refs.notifyresendinvitation.openNotify()
    },
    resendInvitation () {
      const userProfileId = this.$refs.notifyresendinvitation.storeValue
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileResendInvitation', { id: userProfileId, redirect: false }).then(() => {
        this.$eventBus.$emit('trigger-get-users')
      }).finally(() => {
        if (this.$refs.notifyresendinvitation) {
          this.$refs.notifyresendinvitation.closeNotify()
        }
      })
    },
    resetMfa () {
      const userId = this.$refs.popnotifyresetmfa.storeValue
      this.$store.dispatch('authAdminMfaReset', userId).finally(() => {
        this.$eventBus.$emit('stop-reset-mfa')
        this.$refs.popnotifyresetmfa.closeNotify()
      })
    },
    resetPassword () {
      const userId = this.$refs.popnotifyresetpassword.storeValue
      this.$store.dispatch('authAdminPasswordReset', userId).finally(() => {
        this.$eventBus.$emit('stop-reset-password')
        this.$refs.popnotifyresetpassword.closeNotify()
      })
    },
    removeAdmin () {
      const userId = this.$refs.notifyremoveadmin.storeValue
      this.$store.dispatch('lmsSuperadminAdminRemove', userId).then(() => {
        this.$eventBus.$emit('trigger-get-users')
      }).finally(() => {
        this.$refs.notifyremoveadmin.closeNotify()
      })
    },
    removeMasteradmin () {
      const userId = this.$refs.notifyremovemasteradmin.storeValue
      this.$store.dispatch('lmsSuperadminMasteradminRemove', userId).then(() => {
        this.$eventBus.$emit('trigger-get-users')
      }).finally(() => {
        this.$refs.notifyremovemasteradmin.closeNotify()
      })
    },
    removeProfile () {
      const profileId = this.$refs.notifyremoveprofile.storeValue
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileDelete', profileId).then(() => {
        this.$eventBus.$emit('trigger-get-users')
        this.$store.dispatch('defaultNotificationToastsAdd', {
          title: 'Profile deleted',
          message: ``,
          status: 'success'
        })
      }).finally(() => {
        this.$refs.notifyremoveprofile.closeNotify()
      })
    },
    reactivateProfile () {
      const profileId = this.$refs.notifyreactivateprofile.storeValue.id
      this.$store.dispatch('lmsSuperadminWorkspaceUserProfileReactivate', profileId).then(() => {
        this.$eventBus.$emit('trigger-get-users')
        this.$store.dispatch('defaultNotificationToastsAdd', {
          title: 'Profile re-activated',
          message: ``,
          status: 'success'
        })
      }).finally(() => {
        this.$refs.notifyremoveprofile.closeNotify()
      })
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/users/_lmssuperadminuserslisttable.scss";
</style>
